import * as React from 'react'
import { useRef } from 'react'

import { clsx } from 'clsx'

import { useMergeRefs } from '../../hooks/useMergeRefs'
import { Heading } from '../Heading'
import type { HeadingProps } from '../Heading'

import { PageHeaderRowProvider } from './PageHeaderContext'

import styles from './PageHeader.module.css'

type PageHeaderProps = React.HTMLAttributes<HTMLElement> & {
  /**
   * Render a subtle separator line under the header.
   * @default true
   */
  separator?: boolean
}

/**
 * Renders a `<header>`. Place inside `<main>` so the app shell's top-level
 * banner stays the only `banner` landmark. If it must live outside `<main>`,
 * pass `role="region"` + `aria-label` to make it a region landmark instead.
 */
const PageHeaderRoot = React.forwardRef<HTMLElement, PageHeaderProps>(
  ({ children, className, separator = true, ...props }, forwardedRef) => {
    return (
      <header
        ref={forwardedRef}
        className={clsx(
          styles.root,
          separator && styles.withSeparator,
          className,
        )}
        {...props}
      >
        {children}
      </header>
    )
  },
)

PageHeaderRoot.displayName = 'PageHeader'

type PageHeaderRowProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * Maximum width of the row. `wide` (default) matches the standard product
   * page width; `narrow` is for focused single-column layouts; `full` lets
   * the row span the available container.
   * @default 'wide'
   */
  size?: 'narrow' | 'wide' | 'full'
}

const PageHeaderRow = React.forwardRef<HTMLDivElement, PageHeaderRowProps>(
  ({ children, className, size = 'wide', ...props }, forwardedRef) => {
    const observerRef = useRef<HTMLDivElement>(null)
    const mergedRef = useMergeRefs(observerRef, forwardedRef)

    return (
      <PageHeaderRowProvider observerRef={observerRef}>
        <div
          ref={mergedRef}
          className={clsx(
            styles.row,
            size !== 'wide' && styles[size],
            className,
          )}
          {...props}
        >
          {children}
        </div>
      </PageHeaderRowProvider>
    )
  },
)

PageHeaderRow.displayName = 'PageHeader.Row'

type PageHeaderTitleGroupProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * Gap between `TitleLeading` and the title.
   * - `compact` (default): tile/icon avatars with a clean edge.
   * - `comfortable`: image avatars or anything with visual overflow.
   * @default 'compact'
   */
  leadingGap?: 'compact' | 'comfortable'
}

/**
 * Wraps the title block when it has siblings (`TitleLeading`,
 * `TitleAdornments`, `TitleDescription`). A standalone `Title` doesn't
 * need one — drop it directly into a `Row`.
 */
const PageHeaderTitleGroup = React.forwardRef<
  HTMLDivElement,
  PageHeaderTitleGroupProps
>(({ children, className, leadingGap = 'compact', ...props }, forwardedRef) => {
  return (
    <div
      ref={forwardedRef}
      className={clsx(
        styles.titleGroup,
        leadingGap === 'comfortable' && styles.comfortableLeading,
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
})

PageHeaderTitleGroup.displayName = 'PageHeader.TitleGroup'

type PageHeaderTitleLeadingProps = React.HTMLAttributes<HTMLDivElement>

/** Slot to the left of the heading — typically a channel avatar or page icon. */
const PageHeaderTitleLeading = React.forwardRef<
  HTMLDivElement,
  PageHeaderTitleLeadingProps
>(({ children, className, ...props }, forwardedRef) => {
  return (
    <div
      ref={forwardedRef}
      className={clsx(styles.titleLeading, className)}
      {...props}
    >
      {children}
    </div>
  )
})

PageHeaderTitleLeading.displayName = 'PageHeader.TitleLeading'

type PageHeaderTitleProps = Omit<HeadingProps, 'as' | 'size'> & {
  /**
   * Visual size of the title. `lg` (default) is the standard product-page
   * title; `xl` is reserved for deliberately larger, dashboard-style headers
   * (e.g. the Home greeting). Intentionally constrained to these two so page
   * titles stay consistent across the product.
   * @default 'lg'
   */
  size?: 'lg' | 'xl'
}

/**
 * The page `<h1>`. Thin `Heading` wrapper that locks `as="h1"`, defaults to
 * `size="lg"` (opt into `size="xl"` for dashboard-style headers), and forwards
 * everything else. Truncates by default; opt out with `truncate={false}`.
 */
const PageHeaderTitle = React.forwardRef<
  HTMLHeadingElement,
  PageHeaderTitleProps
>(({ truncate = true, size = 'lg', className, ...props }, forwardedRef) => {
  return (
    <Heading
      as="h1"
      size={size}
      truncate={truncate}
      ref={forwardedRef}
      className={clsx(styles.title, className)}
      {...props}
    />
  )
})

PageHeaderTitle.displayName = 'PageHeader.Title'

type PageHeaderTitleAdornmentsProps = React.HTMLAttributes<HTMLSpanElement>

/**
 * Inline elements to the right of the heading (badges, save-view button, etc).
 * Sibling of the `<h1>`, not a child, so the heading stays semantically clean.
 */
const PageHeaderTitleAdornments = React.forwardRef<
  HTMLSpanElement,
  PageHeaderTitleAdornmentsProps
>(({ children, className, ...props }, forwardedRef) => {
  return (
    <span
      ref={forwardedRef}
      className={clsx(styles.titleAdornments, className)}
      {...props}
    >
      {children}
    </span>
  )
})

PageHeaderTitleAdornments.displayName = 'PageHeader.TitleAdornments'

type PageHeaderTitleDescriptionProps = React.HTMLAttributes<HTMLDivElement>

/**
 * Secondary line below the heading, aligned with the title text (not the
 * avatar). Enforces body-size + subtle-colour typography so inner Link/Text
 * components inherit a consistent baseline.
 */
const PageHeaderTitleDescription = React.forwardRef<
  HTMLDivElement,
  PageHeaderTitleDescriptionProps
>(({ children, className, ...props }, forwardedRef) => {
  return (
    <div
      ref={forwardedRef}
      className={clsx(styles.titleDescription, className)}
      {...props}
    >
      {children}
    </div>
  )
})

PageHeaderTitleDescription.displayName = 'PageHeader.TitleDescription'

type PageHeaderActionsProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * Render a vertical divider between adjacent `ActionGroup`s. Off by default
   * to match the Publish header — opt in for dense toolbars.
   */
  separators?: boolean
}

const PageHeaderActions = React.forwardRef<
  HTMLDivElement,
  PageHeaderActionsProps
>(({ children, className, separators = false, ...props }, forwardedRef) => {
  return (
    <div
      ref={forwardedRef}
      className={clsx(
        styles.actions,
        separators && styles.withSeparators,
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
})

PageHeaderActions.displayName = 'PageHeader.Actions'

type PageHeaderActionGroupProps = React.HTMLAttributes<HTMLDivElement>

/**
 * Clusters related controls inside `Actions` so responsive helpers can
 * target them as a unit. Dividers between groups are opt-in via
 * `<Actions separators>`.
 */
const PageHeaderActionGroup = React.forwardRef<
  HTMLDivElement,
  PageHeaderActionGroupProps
>(({ children, className, ...props }, forwardedRef) => {
  return (
    <div
      ref={forwardedRef}
      className={clsx(styles.actionGroup, className)}
      {...props}
    >
      {children}
    </div>
  )
})

PageHeaderActionGroup.displayName = 'PageHeader.ActionGroup'

const PageHeader = Object.assign(PageHeaderRoot, {
  Row: PageHeaderRow,
  TitleGroup: PageHeaderTitleGroup,
  TitleLeading: PageHeaderTitleLeading,
  Title: PageHeaderTitle,
  TitleAdornments: PageHeaderTitleAdornments,
  TitleDescription: PageHeaderTitleDescription,
  Actions: PageHeaderActions,
  ActionGroup: PageHeaderActionGroup,
})

/**
 * Container-query helper classes for hiding controls at specific header widths.
 * Breakpoints stay in sync with `PageHeaderContext`.
 */
const pageHeaderStyles = {
  hideOnNarrow: styles.hideOnNarrow,
  hideOnMedium: styles.hideOnMedium,
  hideOnWide: styles.hideOnWide,
} as const

export { PageHeader, pageHeaderStyles }
export type {
  PageHeaderProps,
  PageHeaderRowProps,
  PageHeaderTitleGroupProps,
  PageHeaderTitleLeadingProps,
  PageHeaderTitleProps,
  PageHeaderTitleAdornmentsProps,
  PageHeaderTitleDescriptionProps,
  PageHeaderActionsProps,
  PageHeaderActionGroupProps,
}
